// Imports
// React and React-specific imports
import { useEffect, useState } from 'react';

// Third-party libraries
import { fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';

// Hooks
import { useAuthContext } from '../contexts/AuthContext';
import { Schema } from '../../amplify/data/resource';
import { useUsernameUniqueness } from '../services/usernameUniqueness/hooks/useUsernameUniqueness';
import { ProfileType, AccountFormData } from '../types/profileTypes';

// Constants
const ACCOUNT_FIELDS = ['email', 'preferredUsername', 'address', 'profileType'] as const;

// Types and Interfaces
interface ProfileFieldProps {
  label: string;
  value: string;
}

interface EditableFieldProps extends ProfileFieldProps {
  onChange: (v: string) => void;
  error?: string;
  type?: string;
  disabled?: boolean;
}

interface UserProfileState {
  id?: string;
  email?: string | null;
  preferredUsername?: string | null;
  address?: string | object | null;
  profileType?: 'musician' | 'venue' | 'fan' | null;
  owner?: string | null;
}

// Helper Functions
const validEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

const validUsername = (username: string) =>
  /^[a-zA-Z]+$/.test(username) && username.length >= 4 && username.length <= 32;

const validProfileType = (type: string) => ['musician', 'venue', 'fan'].includes(type);

// Converts address JSON to 'locality, region' string
const addressToString = (address: string) => {
  try {
    const obj = JSON.parse(address);
    if (obj.locality && obj.region) return `${obj.locality}, ${obj.region}`;
    if (obj.locality) return obj.locality;
    if (obj.region) return obj.region;
    return '';
  } catch {
    return address || '';
  }
};

// Converts 'locality, region' string to address JSON
const stringToAddress = (str: string) => {
  const [locality, region] = str.split(',').map(s => s.trim());
  if (locality && region) return JSON.stringify({ locality, region });
  if (locality) return JSON.stringify({ locality });
  if (region) return JSON.stringify({ region });
  return '';
};

const formatLabel = (key: string) => {
  if (key === 'preferredUsername') return 'Username';
  if (key === 'profileType') return 'Profile Type';
  if (key === 'address') return 'Location';
  return key
    .replace(/^custom:/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

// Sub-Components
const ProfileField = ({ label, value }: ProfileFieldProps) => {
  // Special handling for address field
  if (label === 'Location' && value) {
    try {
      const addressObj = typeof value === 'string' ? JSON.parse(value) : value;
      const locality = addressObj.locality || '';
      const region = addressObj.region || '';
      const formattedAddress =
        locality && region ? `${locality}, ${region}` : locality || region || 'No location';
      return (
        <div className="mb-2 flex flex-col sm:flex-row sm:items-center">
          <span className="block w-full font-semibold text-gray-800 dark:text-gray-200 sm:w-40">
            {label}:
          </span>
          <span className="mt-1 block w-full break-all text-gray-700 dark:text-gray-100 sm:mt-0">
            {formattedAddress}
          </span>
        </div>
      );
    } catch {
      // Fallback to original value if parsing fails
    }
  }

  return (
    <div className="mb-2 flex flex-col sm:flex-row sm:items-center">
      <span className="block w-full font-semibold text-gray-800 dark:text-gray-200 sm:w-40">
        {label}:
      </span>
      <span className="mt-1 block w-full break-all text-gray-700 dark:text-gray-100 sm:mt-0">
        {value}
      </span>
    </div>
  );
};

const EditableField = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  disabled = false,
}: EditableFieldProps) => (
  <div className="mb-4">
    <label className="mb-1 block font-semibold text-gray-800 dark:text-gray-200">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className={`w-full rounded border px-3 py-2 ${error ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-neutral-800 dark:text-gray-100 ${disabled ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-neutral-700' : ''}`}
    />
    {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
  </div>
);

// Component Definition
const ProfilePage: React.FC = () => {
  // State Management
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [userProfile, setUserProfile] = useState<UserProfileState>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editAccount, setEditAccount] = useState(false);
  const [accountForm, setAccountForm] = useState<AccountFormData>({
    email: '',
    preferredUsername: '',
    address: '',
    profileType: 'musician',
  });
  const [accountErrors, setAccountErrors] = useState<Record<string, string>>({});
  const [savingAccount, setSavingAccount] = useState(false);

  // Hooks
  const { isAuthenticated } = useAuthContext();
  const { isUnique: usernameUnique } = useUsernameUniqueness(accountForm.preferredUsername);

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const attrs = await fetchUserAttributes();
        setAttributes(attrs as Record<string, string>);
        // Fetch UserProfile from data store
        const client = generateClient<Schema>();
        const userProfileData = await client.models.UserProfile.list({
          filter: { email: { eq: attrs.email } },
        });
        const profile = userProfileData.data?.[0] || {};
        setUserProfile(profile as UserProfileState);
        setAccountForm({
          email: attrs.email || '',
          preferredUsername: attrs.preferred_username || '',
          address: addressToString(attrs.address || ''),
          profileType: (attrs['custom:profileType'] ||
            profile.profileType ||
            'musician') as ProfileType,
        });
      } catch (error: unknown) {
        setError(
          `Failed to load user profile: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  // Validation Functions
  const validateAccount = () => {
    const errs: Record<string, string> = {};
    if (!validEmail(accountForm.email)) errs.email = 'Invalid email address.';
    if (!validUsername(accountForm.preferredUsername))
      errs.preferredUsername = 'Username must be 4-32 letters.';
    if (
      usernameUnique === false &&
      accountForm.preferredUsername.toLowerCase() !== accountForm.preferredUsername.toLowerCase()
    ) {
      errs.preferredUsername = 'Username is already taken.';
    }
    if (!validProfileType(accountForm.profileType)) errs.profileType = 'Invalid profile type.';
    if (!accountForm.address || accountForm.address.trim() === '') {
      errs.address = 'Location is required.';
    }
    // Optionally, require a comma for both locality and region:
    // if (!accountForm.address || !accountForm.address.includes(',')) {
    //   errs.address = 'Please enter as "City, State".';
    // }
    setAccountErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Event Handlers
  const handleSaveAccount = async () => {
    if (!validateAccount()) return;
    setSavingAccount(true);
    setError(null);
    try {
      await updateUserAttributes({
        userAttributes: {
          preferred_username: accountForm.preferredUsername,
          address: stringToAddress(accountForm.address),
          'custom:profileType': accountForm.profileType,
        },
      });
      const client = generateClient<Schema>();
      if (userProfile?.id) {
        await client.models.UserProfile.update({
          id: userProfile.id,
          email: accountForm.email,
          preferredUsername: accountForm.preferredUsername,
          address: stringToAddress(accountForm.address),
          profileType: accountForm.profileType,
        });
      }
      setEditAccount(false);
    } catch (error: unknown) {
      setError(
        `Failed to update account info: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      setEditAccount(false);
    } finally {
      setSavingAccount(false);
    }
  };

  const handleCancelAccount = () => {
    setAccountForm({
      email: attributes.email || '',
      preferredUsername: attributes.preferred_username || '',
      address: addressToString(attributes.address || ''),
      profileType: (attributes['custom:profileType'] ||
        userProfile.profileType ||
        'musician') as ProfileType,
    });
    setAccountErrors({});
    setEditAccount(false);
  };

  // Render Helpers
  const renderAccountSection = () => (
    <div className="mx-auto h-full w-full rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-neutral-700 dark:bg-neutral-900 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold">Profile</h2>
        {!editAccount ? (
          <button
            className="rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 px-6 py-2 text-lg font-bold text-white transition-opacity hover:opacity-90"
            onClick={() => setEditAccount(true)}
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              className="rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 px-6 py-2 text-lg font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              onClick={handleSaveAccount}
              disabled={savingAccount}
            >
              {savingAccount ? 'Saving...' : 'Save'}
            </button>
            <button
              className="rounded-xl bg-gray-400 px-6 py-2 text-lg font-bold text-white"
              onClick={handleCancelAccount}
              disabled={savingAccount}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {ACCOUNT_FIELDS.map(field =>
          !editAccount ? (
            <ProfileField key={field} label={formatLabel(field)} value={accountForm[field] || ''} />
          ) : field === 'address' ? (
            <div key={field} className="mb-4">
              <label className="mb-1 block font-semibold text-gray-800 dark:text-gray-200">
                {formatLabel(field)}
              </label>
              <input
                type="text"
                value={accountForm[field] || ''}
                onChange={e => setAccountForm(f => ({ ...f, [field]: e.target.value }))}
                disabled={false}
                className={`w-full rounded border px-3 py-2 ${accountErrors[field] ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-neutral-800 dark:text-gray-100`}
              />
              {accountErrors[field] && (
                <div className="mt-1 text-xs text-red-500">{accountErrors[field]}</div>
              )}
            </div>
          ) : (
            <EditableField
              key={field}
              label={formatLabel(field)}
              value={accountForm[field] || ''}
              onChange={v => setAccountForm(f => ({ ...f, [field]: v }))}
              error={accountErrors[field]}
              disabled={field === 'email' || field === 'profileType'}
            />
          )
        )}
      </div>
    </div>
  );

  // Main Return
  return (
    <div className="flex h-[100dvh] flex-col items-center px-6 pb-8 pt-32 sm:px-6 lg:px-8">
      <div className="w-full max-w-7xl flex-grow rounded-xl bg-white p-2 shadow-lg dark:bg-neutral-800">
        {loading && (
          <div className="flex h-full items-center justify-center">
            <div className="text-lg font-semibold">Loading profile...</div>
          </div>
        )}
        {error && <div className="text-center text-red-500">{error}</div>}
        {!loading && !error && renderAccountSection()}
      </div>
    </div>
  );
};

// Export
export default ProfilePage;
